'use client';

import { useEffect, useRef } from 'react';
import { useNotify } from '@/src/lib/notification/notify.ts';
import { isNotificationItem, Notification } from '@/src/app/components/Toast/types.ts';
import { getErrorMessageFromAnyErrorLike } from '@/src/lib/utils/error.ts';
import { NotificationType } from '@/src/app/components/Notification/types.ts';
import { NotificatorProps } from '@/src/app/components/Notificator/notificator.types.ts';
import { GlueErrorResponse } from '@/src/lib/glue/types.ts';
function Notificator({
  newMessages,
  listenToNewMessages = false,
  // Susceptible to server-side re-rendering on the same page, if true
  fallbackMessageType = NotificationType.INFO,
  fallbackMessageAutoCloseOnUnmount = false,
  customNotificationHandlingCallback
}: NotificatorProps) {
  const {
    toastNotification,
    dismissNotification
  } = useNotify();
  const closableOnUnmountNotificationsSet = useRef<Set<string | undefined>>(new Set());
  const generateNotification = (message: Notification | GlueErrorResponse | Error | unknown | string) => {
    if (isNotificationItem(message)) {
      return {
        notification: message,
        isClosableOnUnmount: message.autoCloseOnUnmount ?? false
      };
    }
    return {
      notification: {
        message: getErrorMessageFromAnyErrorLike(message),
        type: fallbackMessageType
      },
      isClosableOnUnmount: fallbackMessageAutoCloseOnUnmount
    };
  };
  useEffect(() => {
    if (!newMessages.length) {
      return;
    }
    newMessages.forEach(message => {
      const {
        notification,
        isClosableOnUnmount
      } = generateNotification(message);
      if (customNotificationHandlingCallback) {
        customNotificationHandlingCallback(notification);
        return;
      }
      const notificationItemData = toastNotification(notification.message, notification.type ?? NotificationType.INFO);
      if (!isClosableOnUnmount) return;
      closableOnUnmountNotificationsSet.current.add(notificationItemData?.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listenToNewMessages ? newMessages : undefined]);
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      closableOnUnmountNotificationsSet.current.forEach(dismissableNotificationId => {
        if (!dismissableNotificationId) return;
        dismissNotification(dismissableNotificationId);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}
export default Notificator;